import React from 'react';
import {
  Redirect,
  Route,
  RouteComponentProps,
  RouteProps,
  useLocation,
} from 'react-router-dom';
import { enforce } from '../../../util/abac';
// @ts-expect-error No types available for react-helmet.
import { Helmet } from 'react-helmet';
import urls from '../../../util/urls';
import LoadingView from '../Loading';
import { useIntl, FormattedMessage } from 'react-intl';
import messages from './data/messages';
import { useSelector, useDispatch } from 'react-redux';
import {
  authSelectors,
  authActions,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/auth';
import { getCookieByName } from '@lifeomic/phc-web-toolkit/src/redux/util/cookieUtils';
import { EXPIRES_COOKIE_NAME } from '@lifeomic/phc-web-toolkit/src/redux/modules/auth/actions';
import { useAuth, hasAuthParams } from 'react-oidc-context';
import { configSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/config';
import { useActiveUser } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveUser';
import { useMixpanel } from '@lifeomic/phc-web-toolkit/src/hooks/useMixpanel';
import { ScreenName } from '@lifeomic/phc-web-toolkit/src/util/analytics';

type PrivateRouteProps = Omit<RouteProps, 'component' | 'render'> & {
  component: React.ComponentType<any>;
  authorized?: boolean;
  intlId: string;
  title: ScreenName;
  project?: string;
  account?: string;
  policy?: Parameters<typeof enforce>[1];
  enforceAction?: Parameters<typeof enforce>[0];
  [prop: string]: unknown;
};

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  component: Component,
  authorized = true,
  intlId,
  title,
  project,
  account,
  policy,
  enforceAction,
  ...toPass
}) => {
  const { formatMessage } = useIntl();
  const auth = useAuth();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const cookieState = useSelector(authSelectors.selectCookieState);
  const config = useSelector(configSelectors.selectConfigState);
  const dispatch = useDispatch<any>();
  const { pathname } = useLocation();
  const { track } = useMixpanel();
  const actorId = useActiveUser()?.id;

  let renderRouteElement: (
    props: RouteComponentProps,
  ) => React.ReactNode = () => null;

  const blockedByPolicy =
    !!enforceAction && !!policy && !enforce(enforceAction, policy);
  const canViewRoute = isAuthenticated && authorized && !blockedByPolicy;

  React.useEffect(() => {
    // Routes can render after auth before fetchUser populates actorId;
    // track no-ops without it, so wait and retry once identity is ready.
    if (canViewRoute && actorId) {
      track('screen_view', { screenName: title });
    }
  }, [canViewRoute, pathname, title, track, actorId]);

  // Locally, we only do the OIDC flow and skip using a separate PHC Login
  // app instance.
  if (!isAuthenticated && !config.oidcAuthEnabled) {
    renderRouteElement = _props => {
      window.location.href = urls.externalApps.login(window.location.href);
      return <LoadingView text={formatMessage(messages.redirectText)} />;
    };
  } else if (!authorized || blockedByPolicy) {
    renderRouteElement = () => <Redirect to={{ pathname: urls.app.home() }} />;
  } else {
    renderRouteElement = props => <Component {...props} {...toPass} />;
  }

  if (
    isAuthenticated &&
    cookieState?.isLoggedInWithCookie &&
    !getCookieByName(EXPIRES_COOKIE_NAME)
  ) {
    dispatch(authActions.logout());
  }

  React.useEffect(() => {
    if (
      config.oidcAuthEnabled &&
      config.oauthClientId &&
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      // Skip the OIDC redirect when redux already has a valid session (e.g.
      // hydrated from /auth/inject). Otherwise an injected token would be
      // immediately replaced by the deployed OIDC flow on the next navigation.
      !isAuthenticated
    ) {
      auth.signinRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.oidcAuthEnabled,
    config.oauthClientId,
    auth.isAuthenticated,
    auth.activeNavigator,
    auth.isLoading,
    auth.signinRedirect,
    isAuthenticated,
  ]);

  return (
    <FormattedMessage
      id={intlId}
      defaultMessage={`${title} - ${project ? '{project} - ' : ''}${
        account ? '{account} - ' : ''
      }PHC`}
      values={{
        project,
        account,
      }}
    >
      {pageTitle => (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
          <Helmet title={pageTitle}></Helmet>
          <Route {...toPass} render={renderRouteElement} />
        </div>
      )}
    </FormattedMessage>
  );
};

export default PrivateRoute;
